import { useQuery } from "@tanstack/react-query";
import {
  ComposedChart,
  Bar,
  Line,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";
import { useSession } from "@/hooks/use-session";
import { supabase } from "@/lib/supabase";
import { SkeletonLoader, EmptyState } from "@/components/system";

/**
 * Daily training load (RPE × duration_min, fallback duration_min). 28-day
 * window with 7-day rolling acute load + 28-day chronic baseline so Jonas
 * can eyeball acute:chronic ratio.
 */

type DailyLoad = { date: string; load: number; acute7: number; chronic28: number };

export function TrainingLoadChart() {
  const { session } = useSession();
  const userId = session?.user?.id;

  const q = useQuery({
    queryKey: ["training_load_28d", userId],
    enabled: Boolean(userId),
    staleTime: 60_000,
    queryFn: async () => {
      const since = new Date();
      since.setDate(since.getDate() - 35);
      const { data, error } = await supabase
        .from("session")
        .select("start_ts, duration_min, rpe")
        .eq("user_id", userId!)
        .gte("start_ts", since.toISOString())
        .order("start_ts");
      if (error) throw error;

      const buckets = new Map<string, number>();
      for (let i = 0; i < 35; i++) {
        const d = new Date();
        d.setDate(d.getDate() - i);
        buckets.set(d.toISOString().slice(0, 10), 0);
      }
      for (const s of data ?? []) {
        const d = new Date(s.start_ts as string).toISOString().slice(0, 10);
        const dur = Number(s.duration_min ?? 0);
        const rpe = Number(s.rpe ?? 5);
        const load = dur * rpe;
        buckets.set(d, (buckets.get(d) ?? 0) + load);
      }
      const sorted = [...buckets.entries()]
        .map(([date, load]) => ({ date, load }))
        .sort((a, b) => (a.date < b.date ? -1 : 1));

      // Compute rolling acute (7d) + chronic (28d) means.
      const out: DailyLoad[] = [];
      for (let i = 0; i < sorted.length; i++) {
        const slice7 = sorted.slice(Math.max(0, i - 6), i + 1);
        const slice28 = sorted.slice(Math.max(0, i - 27), i + 1);
        const acute7 = slice7.reduce((s, x) => s + x.load, 0) / slice7.length;
        const chronic28 = slice28.reduce((s, x) => s + x.load, 0) / slice28.length;
        out.push({ date: sorted[i].date, load: sorted[i].load, acute7, chronic28 });
      }
      // Trim leading buffer days; only display the most recent 28.
      return out.slice(-28);
    },
  });

  if (q.isLoading) return <SkeletonLoader variant="card" height={240} />;
  if (!q.data || q.data.every((d) => d.load === 0)) {
    return (
      <article
        style={{
          background: "var(--bg-2)",
          border: "1px solid var(--hair)",
          borderRadius: "var(--radius-card)",
          padding: "var(--space-5)",
        }}
      >
        <EmptyState headline="No sessions yet." sub="Log a few workouts to see acute vs chronic load." />
      </article>
    );
  }

  const last = q.data[q.data.length - 1];
  const ratio = last.chronic28 > 0 ? last.acute7 / last.chronic28 : 0;
  const ratioTone = ratio > 1.5 || ratio < 0.8 ? "var(--severity-warn)" : "var(--severity-ok)";

  return (
    <article
      style={{
        background: "var(--bg-2)",
        border: "1px solid var(--hair)",
        borderRadius: "var(--radius-card)",
        padding: "var(--space-5)",
      }}
    >
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 12 }}>
        <h3 style={{ margin: 0, fontSize: "var(--type-body)", fontWeight: 500 }}>Training load · 28 days</h3>
        <span className="mono" style={{ fontSize: 12, color: ratioTone }}>
          A/C {ratio.toFixed(2)}
        </span>
      </header>
      <div style={{ width: "100%", height: 200 }}>
        <ResponsiveContainer>
          <ComposedChart data={q.data} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
            <CartesianGrid stroke="var(--hair)" strokeDasharray="2 4" vertical={false} />
            <XAxis
              dataKey="date"
              stroke="var(--fg-4)"
              fontSize={10}
              tickFormatter={(d) => new Date(d as string).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
              tickLine={false}
              axisLine={{ stroke: "var(--hair)" }}
              minTickGap={24}
            />
            <YAxis stroke="var(--fg-4)" fontSize={10} width={36} tickLine={false} axisLine={{ stroke: "var(--hair)" }} />
            <Tooltip
              contentStyle={{ background: "var(--bg-3)", border: "1px solid var(--hair-2)", borderRadius: "var(--radius-card)", fontSize: 12 }}
              labelFormatter={(d) => new Date(d as string).toLocaleDateString(undefined, { dateStyle: "medium" })}
              formatter={(v: unknown) => `${Math.round(Number(v))}`}
            />
            <Bar dataKey="load" name="Daily" fill="var(--accent-wash)" stroke="var(--accent)" />
            <Line type="monotone" dataKey="acute7" name="Acute (7d)" stroke="var(--severity-warn)" strokeWidth={1.5} dot={false} isAnimationActive={false} />
            <Line type="monotone" dataKey="chronic28" name="Chronic (28d)" stroke="var(--fg-3)" strokeWidth={1.5} strokeDasharray="3 3" dot={false} isAnimationActive={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </article>
  );
}
